/**
 * Recu 80 mm (design 4.5) : visible a l'ecran comme apercu, seul element
 * imprime (feuille @media print : tout le reste est masque, page de 80 mm,
 * texte a chasse fixe de 12 px, environ 42 caracteres par ligne, noir sur
 * blanc). La mention « COPIE » d'une reimpression n'est que sur papier.
 */
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import { construireRecu } from './recu';

export const RECU_CLASS = 'recu-80mm';

const PRINT_STYLES = {
    '@media print': {
        '@page': { size: '80mm auto', margin: '3mm' },
        'body *': { visibility: 'hidden !important' },
        [`.${RECU_CLASS}, .${RECU_CLASS} *`]: { visibility: 'visible !important' },
        [`.${RECU_CLASS}`]: {
            position: 'absolute !important', left: 0, top: 0, width: '74mm !important', maxWidth: 'none !important',
            border: 'none !important', boxShadow: 'none !important', padding: '0 !important', margin: '0 !important',
            color: '#000 !important', background: '#fff !important',
        },
    },
};

function Ligne({ gauche, droite, fort = false, retrait = false }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontWeight: fort ? 800 : 400, pl: retrait ? 2 : 0 }}>
            <span>{gauche}</span>
            {droite !== undefined && <span style={{ whiteSpace: 'nowrap' }}>{droite}</span>}
        </Box>
    );
}

function Separateur() {
    return <Box aria-hidden="true" sx={{ borderTop: '1px dashed #000', my: 0.75 }} />;
}

export default function RecuImprimable({ vente, magasin }) {
    const recu = construireRecu(vente, magasin);
    return (
        <>
            <GlobalStyles styles={PRINT_STYLES} />
            <Box
                className={RECU_CLASS}
                data-testid="recu"
                aria-label={`Reçu ${vente.local_number}`}
                sx={{
                    width: '80mm', maxWidth: '100%', p: 2, bgcolor: '#fff', color: '#000', border: '1px solid #E2E8F0', borderRadius: 1,
                    fontFamily: '"DejaVu Sans Mono", "Courier New", monospace', fontSize: 12, lineHeight: 1.35,
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    {recu.entete.map((l, i) => (
                        <Box key={`e${i}`} sx={{ fontWeight: i === 0 ? 800 : 400 }}>{l}</Box>
                    ))}
                    <Box data-testid="recu-mention" sx={{ fontWeight: 800, mt: 1 }}>{recu.mention}</Box>
                    {/* Sur papier seulement : l'apercu montre le recu deja remis au client. */}
                    {recu.reimpression && (
                        <Box data-testid="recu-copie" sx={{ fontWeight: 800, display: 'none', '@media print': { display: 'block' } }}>
                            {recu.reimpression}
                        </Box>
                    )}
                </Box>
                <Ligne gauche={recu.numero} droite={recu.date} />
                <div>{recu.caissier}</div>
                <Separateur />
                {recu.articles.map((a) => (
                    <Box key={a.line_no} data-testid={`recu-article-${a.line_no}`}>
                        {a.detail ? (
                            <>
                                <div>{a.libelle}</div>
                                <Ligne gauche={a.detail} droite={a.montant} retrait />
                            </>
                        ) : (
                            <Ligne gauche={a.libelle} droite={a.montant} />
                        )}
                        {a.prix_modifie && <Ligne gauche={a.prix_modifie} retrait />}
                        {a.rabais && <Ligne gauche={a.rabais.libelle} droite={a.rabais.montant} retrait />}
                    </Box>
                ))}
                <Separateur />
                <div>{recu.articles_compte}</div>
                {recu.totaux.map((t) => (
                    <Box key={t.libelle} data-testid={`recu-total-${t.libelle}`}>
                        <Ligne gauche={t.libelle} droite={t.montant} fort={t.fort} />
                    </Box>
                ))}
                <Separateur />
                <Box data-testid="recu-taxes">{recu.taxes.map((l, i) => <div key={`t${i}`}>{l}</div>)}</Box>
                <Box sx={{ mt: 1, textAlign: 'center' }}>{recu.pied.map((l, i) => <div key={`p${i}`}>{l}</div>)}</Box>
            </Box>
        </>
    );
}
